/**
 * Talent Tree Manager
 *
 * Handles all talent tree logic:
 * - Point calculation from skill levels
 * - Node unlocking and validation
 * - Respec with gold cost
 * - Bonus calculations with mastery multipliers
 */

import { gameState } from '../../game/gameState.js';
import { getTalentTreeData, NODE_STATES } from './talentTreeData.js';
import { SKILLS } from '../../game/constants.js';

// Transient stacking speed tracker (resets on page reload - that's fine)
// Maps skillId -> { count: number, lastActionId: string }
const stackingSpeedState = new Map();

// Post-unlock callbacks: Array of (skillId, nodeId, newRank) => void
const onUnlockCallbacks = [];

/**
 * Register a callback to fire after a talent node is unlocked
 * @param {Function} callback - (skillId, nodeId, newRank) => void
 */
export function onTalentUnlocked(callback) {
    onUnlockCallbacks.push(callback);
}

/**
 * Calculate how many talent points a skill has earned based on level
 * All ranks obtainable by level 90, capped at totalRanks
 */
export function getEarnedPoints(skillId) {
    const tree = getTalentTreeData(skillId);
    if (!tree) return 0;

    const skill = gameState.getSkill(skillId);
    if (!skill) return 0;

    // Points per level = totalRanks / 90
    // E.g., 21 ranks / 90 = 0.2333 points per level
    // At level 90: floor(90 * 0.2333) = 21 points
    // Cap at totalRanks to prevent excess points at levels 91-99
    const pointsPerLevel = tree.totalRanks / 90;
    return Math.min(tree.totalRanks, Math.floor(skill.level * pointsPerLevel));
}

/**
 * Get available (unspent) points for a skill's talent tree
 */
export function getAvailablePoints(skillId) {
    const earned = getEarnedPoints(skillId);
    const spent = getSpentPoints(skillId);
    return Math.max(0, earned - spent);
}

/**
 * Get total spent points in a talent tree
 */
export function getSpentPoints(skillId) {
    const treeData = gameState.getTalentTree(skillId);
    if (!treeData || !treeData.nodes) return 0;

    return Object.values(treeData.nodes).reduce((sum, rank) => sum + rank, 0);
}

/**
 * Get the next level at which a talent point will be earned
 * Returns null if all points have been earned (level 90+)
 */
export function getNextTalentPointLevel(skillId) {
    const tree = getTalentTreeData(skillId);
    if (!tree) return null;

    const skill = gameState.getSkill(skillId);
    if (!skill) return null;

    const currentLevel = skill.level;
    const totalRanks = tree.totalRanks;
    const pointsPerLevel = totalRanks / 90;

    // All points earned at level 90
    if (currentLevel >= 90) return null;

    // Current earned points
    const currentPoints = Math.floor(currentLevel * pointsPerLevel);

    // Find next level where points increase
    // Next point at: ceil((currentPoints + 1) / pointsPerLevel)
    const nextLevel = Math.ceil((currentPoints + 1) / pointsPerLevel);

    // Cap at 90 (all points earned by then)
    return Math.min(nextLevel, 90);
}

/**
 * Get the current rank of a specific node
 */
export function getNodeRank(skillId, nodeId) {
    const treeData = gameState.getTalentTree(skillId);
    if (!treeData || !treeData.nodes) return 0;
    return treeData.nodes[nodeId] || 0;
}

/**
 * Get the state of a node (locked, available, purchased, maxed)
 */
export function getNodeState(skillId, nodeId) {
    const tree = getTalentTreeData(skillId);
    if (!tree) return NODE_STATES.LOCKED;

    const node = tree.nodes[nodeId];
    if (!node) return NODE_STATES.LOCKED;

    const currentRank = getNodeRank(skillId, nodeId);

    // Check if maxed
    if (currentRank >= node.maxRank) {
        return NODE_STATES.MAXED;
    }

    // Check if purchased (but not maxed)
    if (currentRank > 0) {
        return NODE_STATES.PURCHASED;
    }

    // Check prerequisites
    const prereqsMet = checkPrerequisites(skillId, nodeId);
    if (!prereqsMet) {
        return NODE_STATES.LOCKED;
    }

    // Check if has points
    const availablePoints = getAvailablePoints(skillId);
    if (availablePoints > 0) {
        return NODE_STATES.AVAILABLE;
    }

    // Prerequisites met but no points
    return NODE_STATES.LOCKED;
}

/**
 * Check if prerequisites for a node are met
 * Prerequisites must be at max rank
 * Supports requirePrerequisites property for "X of N" requirements
 */
export function checkPrerequisites(skillId, nodeId) {
    const tree = getTalentTreeData(skillId);
    if (!tree) return false;

    const node = tree.nodes[nodeId];
    if (!node) return false;

    // No prerequisites = always available (foundation node)
    if (!node.prerequisites || node.prerequisites.length === 0) {
        return true;
    }

    // Count how many prerequisites are met (at max rank)
    let metCount = 0;
    for (const prereqId of node.prerequisites) {
        const prereqNode = tree.nodes[prereqId];
        if (!prereqNode) continue;

        const prereqRank = getNodeRank(skillId, prereqId);
        if (prereqRank >= prereqNode.maxRank) {
            metCount++;
        }
    }

    // If requirePrerequisites is set, only need that many (e.g., 2 of 3)
    // Otherwise, require ALL prerequisites
    const requiredCount = node.requirePrerequisites || node.prerequisites.length;
    return metCount >= requiredCount;
}

/**
 * Attempt to unlock/upgrade a node
 * Returns { success: boolean, reason?: string }
 */
export function unlockNode(skillId, nodeId) {
    const tree = getTalentTreeData(skillId);
    if (!tree) {
        return { success: false, reason: 'No talent tree for this skill' };
    }

    const node = tree.nodes[nodeId];
    if (!node) {
        return { success: false, reason: 'Invalid node' };
    }

    // Check current rank
    const currentRank = getNodeRank(skillId, nodeId);
    if (currentRank >= node.maxRank) {
        return { success: false, reason: 'Node already maxed' };
    }

    // Check prerequisites
    if (!checkPrerequisites(skillId, nodeId)) {
        return { success: false, reason: 'Prerequisites not met' };
    }

    // Check available points
    const availablePoints = getAvailablePoints(skillId);
    if (availablePoints < 1) {
        return { success: false, reason: 'No points available' };
    }

    // Unlock the node
    const newRank = currentRank + 1;
    gameState.setTalentNodeRank(skillId, nodeId, newRank);

    // Fire post-unlock callbacks
    for (const cb of onUnlockCallbacks) {
        try { cb(skillId, nodeId, newRank); } catch (e) { /* ignore */ }
    }

    return {
        success: true,
        newRank,
        maxRank: node.maxRank
    };
}

/**
 * Calculate respec cost for a single node rank
 * Cost scales based on node tier
 */
export function getRespecCostForNode(skillId, nodeId) {
    const tree = getTalentTreeData(skillId);
    if (!tree) return 0;

    const node = tree.nodes[nodeId];
    if (!node) return 0;

    // Base cost per tier: T1=500, T2=1000, T3=2500, T4=5000
    const tierCosts = { 1: 500, 2: 1000, 3: 2500, 4: 5000 };
    return tierCosts[node.tier] || 1000;
}

/**
 * Calculate total respec cost for all spent points
 * Full reset gets 20% discount
 */
export function getFullRespecCost(skillId) {
    const tree = getTalentTreeData(skillId);
    if (!tree) return 0;

    const treeData = gameState.getTalentTree(skillId);
    if (!treeData || !treeData.nodes) return 0;

    let totalCost = 0;
    for (const [nodeId, rank] of Object.entries(treeData.nodes)) {
        const costPerRank = getRespecCostForNode(skillId, nodeId);
        totalCost += costPerRank * rank;
    }

    // 20% discount for full reset
    return Math.floor(totalCost * 0.8);
}

/**
 * Refund a single rank from a node
 * Returns { success: boolean, reason?: string, goldCost?: number }
 */
export function refundNodeRank(skillId, nodeId) {
    const tree = getTalentTreeData(skillId);
    if (!tree) {
        return { success: false, reason: 'No talent tree for this skill' };
    }

    const node = tree.nodes[nodeId];
    if (!node) {
        return { success: false, reason: 'Invalid node' };
    }

    const currentRank = getNodeRank(skillId, nodeId);
    if (currentRank <= 0) {
        return { success: false, reason: 'Node has no ranks to refund' };
    }

    // Check if any other nodes depend on this one being maxed
    if (!canRefundNode(skillId, nodeId)) {
        return { success: false, reason: 'Other nodes depend on this one' };
    }

    // Check if level 99 (free respec)
    const skill = gameState.getSkill(skillId);
    const isFreeRespec = skill && skill.level >= 99;

    const goldCost = isFreeRespec ? 0 : getRespecCostForNode(skillId, nodeId);

    // Check gold
    if (goldCost > 0 && gameState.getGold() < goldCost) {
        return { success: false, reason: `Need ${goldCost} gold` };
    }

    // Deduct gold
    if (goldCost > 0) {
        gameState.removeGold(goldCost);
    }

    // Refund the rank
    gameState.setTalentNodeRank(skillId, nodeId, currentRank - 1);

    return {
        success: true,
        newRank: currentRank - 1,
        goldCost
    };
}

/**
 * Check if a node can be refunded without breaking dependencies
 */
export function canRefundNode(skillId, nodeId) {
    const tree = getTalentTreeData(skillId);
    if (!tree) return false;

    const node = tree.nodes[nodeId];
    if (!node) return false;

    const currentRank = getNodeRank(skillId, nodeId);

    // Check each node that has this as a prerequisite
    for (const [otherNodeId, otherNode] of Object.entries(tree.nodes)) {
        if (otherNode.prerequisites && otherNode.prerequisites.includes(nodeId)) {
            const otherRank = getNodeRank(skillId, otherNodeId);
            // If the dependent node has any ranks, we can't refund below max
            if (otherRank > 0 && currentRank <= node.maxRank) {
                return false;
            }
        }
    }

    return true;
}

/**
 * Full respec - refund all nodes
 * Returns { success: boolean, reason?: string, goldCost?: number, refundedPoints: number }
 */
export function fullRespec(skillId) {
    const tree = getTalentTreeData(skillId);
    if (!tree) {
        return { success: false, reason: 'No talent tree for this skill' };
    }

    const spentPoints = getSpentPoints(skillId);
    if (spentPoints === 0) {
        return { success: false, reason: 'No points to refund' };
    }

    // Check if level 99 (free respec)
    const skill = gameState.getSkill(skillId);
    const isFreeRespec = skill && skill.level >= 99;

    const goldCost = isFreeRespec ? 0 : getFullRespecCost(skillId);

    // Check gold
    if (goldCost > 0 && gameState.getGold() < goldCost) {
        return { success: false, reason: `Need ${goldCost} gold` };
    }

    // Deduct gold
    if (goldCost > 0) {
        gameState.removeGold(goldCost);
    }

    // Clear all nodes
    gameState.resetTalentTree(skillId);

    return {
        success: true,
        goldCost,
        refundedPoints: spentPoints
    };
}

// ========================================
// BONUS CALCULATIONS
// ========================================

/**
 * Get mastery multiplier for talent effects
 * Level 99: +50% (1.5x)
 * Level 120: +100% (2x)
 */
export function getMasteryMultiplier(skillId) {
    const skill = gameState.getSkill(skillId);
    if (!skill) return 1;

    if (skill.level >= 120) return 2.0;
    if (skill.level >= 99) return 1.5;
    return 1.0;
}

/**
 * Calculate total bonus of a specific type from talents
 * Includes mastery multiplier
 */
export function getTalentBonus(skillId, bonusType, subType = null) {
    const tree = getTalentTreeData(skillId);
    if (!tree) return 0;

    const treeData = gameState.getTalentTree(skillId);
    if (!treeData || !treeData.nodes) return 0;

    let totalBonus = 0;

    for (const [nodeId, rank] of Object.entries(treeData.nodes)) {
        if (rank <= 0) continue;

        const node = tree.nodes[nodeId];
        if (!node) continue;

        for (const effect of node.effects) {
            if (effect.type === bonusType) {
                // If subType filter specified, only include effects with matching subType
                if (subType && effect.subType && effect.subType !== subType) continue;
                const value = effect.perRank ? effect.value * rank : effect.value;
                totalBonus += value;
            }
        }
    }

    // Apply global bonus if present (Master Miner talent)
    const globalBonus = getGlobalTalentBonus(skillId);
    if (globalBonus > 0 && totalBonus > 0) {
        totalBonus *= (1 + globalBonus);
    }

    // Apply mastery multiplier
    const masteryMult = getMasteryMultiplier(skillId);
    return totalBonus * masteryMult;
}

/**
 * Get global bonus multiplier (from "All bonuses +X%" talents)
 */
export function getGlobalTalentBonus(skillId) {
    const tree = getTalentTreeData(skillId);
    if (!tree) return 0;

    const treeData = gameState.getTalentTree(skillId);
    if (!treeData || !treeData.nodes) return 0;

    let globalBonus = 0;

    for (const [nodeId, rank] of Object.entries(treeData.nodes)) {
        if (rank <= 0) continue;

        const node = tree.nodes[nodeId];
        if (!node) continue;

        for (const effect of node.effects) {
            if (effect.type === 'globalBonus') {
                const value = effect.perRank ? effect.value * rank : effect.value;
                globalBonus += value;
            }
        }
    }

    return globalBonus;
}

/**
 * Get XP bonus from talents (for experience.js integration)
 */
export function getTalentXPBonus(skillId) {
    return getTalentBonus(skillId, 'xpBonus');
}

/**
 * Get speed bonus from talents (for tick.js integration)
 * @param {string} skillId - Skill ID
 * @param {string|null} subType - Optional subType filter (e.g., 'cleaning', 'brewing')
 */
export function getTalentSpeedBonus(skillId, subType = null) {
    return getTalentBonus(skillId, 'speedBonus', subType);
}

/**
 * Get tool effectiveness bonus
 */
export function getTalentToolBonus(skillId) {
    return getTalentBonus(skillId, 'toolBonus');
}

/**
 * Get loot quantity bonus from talents (for drops.js integration)
 */
export function getTalentLootBonus(skillId) {
    return getTalentBonus(skillId, 'lootBonus');
}

/**
 * Get flat bonus of a specific type from talents
 * Used for things like ritual XP multiplier bonuses
 */
export function getTalentFlatBonus(skillId, bonusName) {
    const tree = getTalentTreeData(skillId);
    if (!tree) return 0;

    const treeData = gameState.getTalentTree(skillId);
    if (!treeData || !treeData.nodes) return 0;

    let totalBonus = 0;

    for (const [nodeId, rank] of Object.entries(treeData.nodes)) {
        if (rank <= 0) continue;

        const node = tree.nodes[nodeId];
        if (!node) continue;

        for (const effect of node.effects) {
            if (effect.type === 'flatBonus' && effect.bonus === bonusName) {
                const value = effect.perRank ? effect.value * rank : effect.value;
                totalBonus += value;
            }
        }
    }

    // Apply mastery multiplier
    const masteryMult = getMasteryMultiplier(skillId);
    return totalBonus * masteryMult;
}

/**
 * Get proc chance for a specific proc type
 */
export function getTalentProcChance(skillId, procType) {
    const tree = getTalentTreeData(skillId);
    if (!tree) return 0;

    const treeData = gameState.getTalentTree(skillId);
    if (!treeData || !treeData.nodes) return 0;

    let totalChance = 0;

    for (const [nodeId, rank] of Object.entries(treeData.nodes)) {
        if (rank <= 0) continue;

        const node = tree.nodes[nodeId];
        if (!node) continue;

        for (const effect of node.effects) {
            if (effect.type === 'procChance' && effect.proc === procType) {
                const value = effect.perRank ? effect.value * rank : effect.value;
                totalChance += value;
            }
        }
    }

    // Apply mastery multiplier to proc chances
    const masteryMult = getMasteryMultiplier(skillId);
    return totalChance * masteryMult;
}

/**
 * Roll for a proc effect
 * Returns true if proc triggers
 */
export function rollTalentProc(skillId, procType, bonusMultiplier = 0) {
    let chance = getTalentProcChance(skillId, procType);
    if (chance <= 0) return false;
    // Apply bonus multiplier (e.g., Grand Prospector's +25% to all drop chances)
    if (bonusMultiplier > 0) {
        chance *= (1 + bonusMultiplier);
    }
    return Math.random() < chance;
}

/**
 * Get global rare loot bonus from Husbandry Treasure Nose talent
 * This bonus applies to ALL skills' rare drop chances
 * @returns {number} Flat bonus to rare drop chance (e.g., 0.06 for 6%)
 */
export function getGlobalRareLootBonus() {
    return getTalentFlatBonus(SKILLS.HUSBANDRY, 'globalRareLoot');
}

/**
 * Roll for global rare loot bonus
 * Call this to check if Treasure Nose triggers a bonus rare drop
 * @returns {boolean} true if bonus rare drop should be awarded
 */
export function rollGlobalRareLoot() {
    const bonus = getGlobalRareLootBonus();
    if (bonus <= 0) return false;
    return Math.random() < bonus;
}

/**
 * Get all node states for rendering
 */
export function getAllNodeStates(skillId) {
    const tree = getTalentTreeData(skillId);
    if (!tree) return {};

    const states = {};
    for (const nodeId of Object.keys(tree.nodes)) {
        states[nodeId] = getNodeState(skillId, nodeId);
    }
    return states;
}

/**
 * Get summary of talent bonuses for tooltip display
 */
export function getTalentBonusSummary(skillId) {
    const bonuses = [];

    const xpBonus = getTalentXPBonus(skillId);
    if (xpBonus > 0) {
        bonuses.push({ label: 'XP Bonus', value: `+${(xpBonus * 100).toFixed(0)}%` });
    }

    const speedBonus = getTalentSpeedBonus(skillId);
    if (speedBonus > 0) {
        bonuses.push({ label: 'Speed Bonus', value: `+${(speedBonus * 100).toFixed(0)}%` });
    }

    const toolBonus = getTalentToolBonus(skillId);
    if (toolBonus > 0) {
        bonuses.push({ label: 'Tool Bonus', value: `+${(toolBonus * 100).toFixed(0)}%` });
    }

    const lootBonus = getTalentLootBonus(skillId);
    if (lootBonus > 0) {
        bonuses.push({ label: 'Loot Bonus', value: `+${(lootBonus * 100).toFixed(0)}%` });
    }

    // Proc chances (all skill types)
    const procTypes = [
        // Mining
        'doubleOre', 'gemDrop', 'bonusOre', 'pureVein', 'motherlode', 'jackpot', 'autoSmelt',
        // Smithing
        'coalSave', 'resourceSave', 'extraBar', 'doubleOutput', 'forgeInstant', 'bulkCommission',
        // Woodcutting
        'seedDrop', 'ancientSeedDrop', 'doubleLogs', 'timberProc',
        // Pyre
        'logSave', 'boneSave', 'bonusAshes', 'veilDrop', 'instantBurn', 'cinderStorm', 'veilBlessing',
        // Alchemy
        'herbSave', 'bonusHerb', 'doubleBrew', 'bubblingBrew', 'brewHerbSave', 'transmutation', 'fiveXHerbs', 'potionSaver', 'lingeringEssence',
        // Crafting
        'materialSave', 'goldWaive', 'doubleOutput', 'bulkProduce', 'patronsFavor', 'inspiredCraft',
        // Fishing
        'doubleFish', 'bycatch', 'autoCook', 'luckyHaul', 'instantCatch', 'feedingFrenzy',
        // Cooking
        'ingredientSave', 'doubleFood', 'secondHelping', 'banquet',
        // Gatherer
        'petAssist', 'seedFind', 'hiddenCache', 'tenXDrops', 'materialTransmutation',
        // Pilfering
        'shadowClone', 'phantomStrike', 'tenXCoins', 'bonusLootFind', 'vanishingAct', 'goldenOpportunity',
        // Glyphbinding
        'ichorSave', 'doubleGlyph', 'tripleGlyph', 'ichorBurst',
        // Bounty
        'doubleContractReward', 'surplusMaterial',
        // Husbandry
        'doubleContractXP', 'rapidDeployment', 'packInstinct', 'giftGiver', 'combatCompanion',
        // Farming
        'fastGrowth', 'secondHarvest', 'seedReclaim', 'instantGrowth', 'bonusSeedDrop', 'freeSeed',
        // Architecting
        'preserveMaterials',
        // Woodworking
        'bonusArrows', 'arrowStorm', 'instantCraft', 'bonusIchor', 'veilTimber',
        // Combat
        'doubleStrike', 'ammoSave', 'multiShot', 'elementalCascade'
    ];
    for (const proc of procTypes) {
        const chance = getTalentProcChance(skillId, proc);
        if (chance > 0) {
            const label = proc.replace(/([A-Z])/g, ' $1').trim();
            bonuses.push({ label: `${label} Chance`, value: `${(chance * 100).toFixed(0)}%` });
        }
    }

    // Flat bonuses
    const ritualXpBonus = getTalentFlatBonus(skillId, 'ritualXpBonus');
    if (ritualXpBonus > 0) {
        bonuses.push({ label: 'Ritual XP Bonus', value: `+${(ritualXpBonus * 100).toFixed(0)}%` });
    }

    const potionDuration = getTalentFlatBonus(skillId, 'potionDuration');
    if (potionDuration > 0) {
        bonuses.push({ label: 'Potion Duration', value: `+${(potionDuration * 100).toFixed(0)}%` });
    }

    const bloomAutoDeposit = getTalentFlatBonus(skillId, 'bloomAutoDeposit');
    if (bloomAutoDeposit > 0) {
        bonuses.push({ label: 'Bloom Auto-Deposit', value: 'Enabled' });
    }

    const bonusMaterialChance = getTalentFlatBonus(skillId, 'bonusMaterialChance');
    if (bonusMaterialChance > 0) {
        bonuses.push({ label: 'Bonus Material Chance', value: `+${(bonusMaterialChance * 100).toFixed(0)}%` });
    }

    // Gatherer flat bonuses (3-branch rework)
    const herbTierUpgrade = getTalentFlatBonus(skillId, 'herbTierUpgrade');
    if (herbTierUpgrade > 0) {
        bonuses.push({ label: 'Herb Attunement', value: `+${herbTierUpgrade} tier` });
    }

    const awakeningChanceBonus = getTalentFlatBonus(skillId, 'awakeningChanceBonus');
    if (awakeningChanceBonus > 0) {
        bonuses.push({ label: 'Awakening Material Bonus', value: `+${(awakeningChanceBonus * 100).toFixed(0)}%` });
    }

    const momentumYieldBonus = getTalentFlatBonus(skillId, 'momentumYieldBonus');
    if (momentumYieldBonus > 0) {
        bonuses.push({ label: 'Momentum Yield (at max stack)', value: `+${(momentumYieldBonus * 100).toFixed(0)}%` });
    }

    // Pilfering flat bonuses
    const pilfererSpeed = getTalentFlatBonus(skillId, 'pilfererSpeed');
    if (pilfererSpeed > 0) {
        bonuses.push({ label: 'Pilferer Speed', value: `+${(pilfererSpeed * 100).toFixed(0)}%` });
    }

    const killContractAssist = getTalentFlatBonus(skillId, 'killContractAssist');
    if (killContractAssist > 0) {
        bonuses.push({ label: 'Kill Contract Assist', value: `+${(killContractAssist * 100).toFixed(0)}%` });
    }

    const globalCoinBonus = getTalentFlatBonus(skillId, 'globalCoinBonus');
    if (globalCoinBonus > 0) {
        bonuses.push({ label: 'Global Coin Bonus', value: `+${(globalCoinBonus * 100).toFixed(0)}%` });
    }

    const shopSellBonus = getTalentFlatBonus(skillId, 'shopSellBonus');
    if (shopSellBonus > 0) {
        bonuses.push({ label: 'Shop Sell Prices', value: `+${(shopSellBonus * 100).toFixed(0)}%` });
    }

    // Glyphbinding flat bonuses
    const bonusIchor = getTalentFlatBonus(skillId, 'bonusIchor');
    if (bonusIchor > 0) {
        bonuses.push({ label: 'Bonus Ichor Chance', value: `+${(bonusIchor * 100).toFixed(0)}%` });
    }

    const ashesSuccessRate = getTalentFlatBonus(skillId, 'ashesSuccessRate');
    if (ashesSuccessRate > 0) {
        bonuses.push({ label: 'Ashes Success Rate', value: `+${(ashesSuccessRate * 100).toFixed(0)}%` });
    }

    const arcaneSurge = getTalentFlatBonus(skillId, 'arcaneSurge');
    if (arcaneSurge > 0) {
        bonuses.push({ label: 'Arcane Surge', value: 'Active' });
    }

    const glyphCraftSpeed = getTalentFlatBonus(skillId, 'glyphCraftSpeed');
    if (glyphCraftSpeed > 0) {
        bonuses.push({ label: 'Glyph Craft Speed', value: `+${(glyphCraftSpeed * 100).toFixed(0)}%` });
    }

    const glyphOutputBonus = getTalentFlatBonus(skillId, 'glyphOutputBonus');
    if (glyphOutputBonus > 0) {
        bonuses.push({ label: 'Glyph Output Bonus', value: `+${(glyphOutputBonus * 100).toFixed(0)}%` });
    }

    // Bounty flat bonuses
    const contractGoldBonus = getTalentFlatBonus(skillId, 'contractGoldBonus');
    if (contractGoldBonus > 0) {
        bonuses.push({ label: 'Contract Gold Bonus', value: `+${(contractGoldBonus * 100).toFixed(0)}%` });
    }

    const contractRequirementReduction = getTalentFlatBonus(skillId, 'contractRequirementReduction');
    if (contractRequirementReduction > 0) {
        bonuses.push({ label: 'Requirement Reduction', value: `-${(contractRequirementReduction * 100).toFixed(0)}%` });
    }

    const gatheringContractXP = getTalentFlatBonus(skillId, 'gatheringContractXP');
    if (gatheringContractXP > 0) {
        bonuses.push({ label: 'Gathering Contract XP', value: `+${(gatheringContractXP * 100).toFixed(0)}%` });
    }

    const killContractXP = getTalentFlatBonus(skillId, 'killContractXP');
    if (killContractXP > 0) {
        bonuses.push({ label: 'Kill Contract XP', value: `+${(killContractXP * 100).toFixed(0)}%` });
    }

    const guildAssistBonus = getTalentFlatBonus(skillId, 'guildAssistBonus');
    if (guildAssistBonus > 0) {
        bonuses.push({ label: 'Guild Assist Bonus', value: `+${(guildAssistBonus * 100).toFixed(0)}%` });
    }

    const reputationStreak = getTalentFlatBonus(skillId, 'reputationStreak');
    if (reputationStreak > 0) {
        bonuses.push({ label: 'Reputation Streak', value: `+${(reputationStreak * 100).toFixed(0)}% per delivery` });
    }

    const masterContractorBonus = getTalentFlatBonus(skillId, 'masterContractorBonus');
    if (masterContractorBonus > 0) {
        bonuses.push({ label: 'Master Contractor Bonus', value: `+${(masterContractorBonus * 100).toFixed(0)}%` });
    }

    const highTierGoldBonus = getTalentFlatBonus(skillId, 'highTierGoldBonus');
    if (highTierGoldBonus > 0) {
        bonuses.push({ label: 'High Tier Gold (T4+)', value: `+${(highTierGoldBonus * 100).toFixed(0)}%` });
    }

    const bulkProcessor = getTalentFlatBonus(skillId, 'bulkProcessor');
    if (bulkProcessor > 0) {
        bonuses.push({ label: 'Bulk Progress', value: `+${(bulkProcessor * 100).toFixed(0)}%` });
    }

    const specializedReduction = getTalentFlatBonus(skillId, 'specializedReduction');
    if (specializedReduction > 0) {
        bonuses.push({ label: 'Specialized Reduction', value: `-${(specializedReduction * 100).toFixed(0)}%` });
    }

    const fortuneFavor = getTalentFlatBonus(skillId, 'fortuneFavor');
    if (fortuneFavor > 0) {
        bonuses.push({ label: "Fortune's Favor", value: '5x Jackpot' });
    }

    const megaContracts = getTalentFlatBonus(skillId, 'megaContracts');
    if (megaContracts > 0) {
        bonuses.push({ label: 'Mega Contracts', value: 'Unlocked' });
    }

    const contractMomentum = getTalentFlatBonus(skillId, 'contractMomentum');
    if (contractMomentum > 0) {
        bonuses.push({ label: 'Contract Momentum', value: `+${(contractMomentum * 100).toFixed(0)}%` });
    }

    const dedicatedSupplier = getTalentFlatBonus(skillId, 'dedicatedSupplier');
    if (dedicatedSupplier > 0) {
        bonuses.push({ label: 'Dedicated Supplier', value: 'Pin 1 Contract' });
    }

    // Farming flat bonuses
    const growthTimeReduction = getTalentFlatBonus(skillId, 'growthTimeReduction');
    if (growthTimeReduction > 0) {
        bonuses.push({ label: 'Growth Time Reduction', value: `-${(growthTimeReduction * 100).toFixed(0)}%` });
    }

    const seedDropBonus = getTalentFlatBonus(skillId, 'seedDropBonus');
    if (seedDropBonus > 0) {
        bonuses.push({ label: 'Seed Drop Bonus', value: `+${(seedDropBonus * 100).toFixed(0)}%` });
    }

    const herbYieldBonus = getTalentFlatBonus(skillId, 'herbYieldBonus');
    if (herbYieldBonus > 0) {
        bonuses.push({ label: 'Herb Yield Bonus', value: `+${(herbYieldBonus * 100).toFixed(0)}%` });
    }

    const treeYieldBonus = getTalentFlatBonus(skillId, 'treeYieldBonus');
    if (treeYieldBonus > 0) {
        bonuses.push({ label: 'Tree Yield Bonus', value: `+${(treeYieldBonus * 100).toFixed(0)}%` });
    }

    const treeGrowthReduction = getTalentFlatBonus(skillId, 'treeGrowthReduction');
    if (treeGrowthReduction > 0) {
        bonuses.push({ label: 'Tree Growth Reduction', value: `-${(treeGrowthReduction * 100).toFixed(0)}%` });
    }

    const cropYieldBonus = getTalentFlatBonus(skillId, 'cropYieldBonus');
    if (cropYieldBonus > 0) {
        bonuses.push({ label: 'Crop Yield Bonus', value: `+${(cropYieldBonus * 100).toFixed(0)}%` });
    }

    const verdantGrowthReduction = getTalentFlatBonus(skillId, 'verdantGrowthReduction');
    if (verdantGrowthReduction > 0) {
        bonuses.push({ label: 'Verdant Growth Reduction', value: `-${(verdantGrowthReduction * 100).toFixed(0)}%` });
    }

    const abundantYieldBonus = getTalentFlatBonus(skillId, 'abundantYieldBonus');
    if (abundantYieldBonus > 0) {
        bonuses.push({ label: 'Abundant Yield Bonus', value: `+${(abundantYieldBonus * 100).toFixed(0)}%` });
    }

    const autoReplant = getTalentFlatBonus(skillId, 'autoReplant');
    if (autoReplant > 0) {
        bonuses.push({ label: 'Auto-Replant', value: 'Enabled' });
    }

    // Architecting flat bonuses
    const archContractGoldBonus = getTalentFlatBonus(skillId, 'contractGoldBonus');
    if (archContractGoldBonus > 0 && skillId === 'architecting') {
        bonuses.push({ label: 'Civic Contract Gold', value: `+${(archContractGoldBonus * 100).toFixed(0)}%` });
    }

    const contractXPBonus = getTalentFlatBonus(skillId, 'contractXPBonus');
    if (contractXPBonus > 0) {
        bonuses.push({ label: 'Contract XP Bonus', value: `+${(contractXPBonus * 100).toFixed(0)}%` });
    }

    const contractSpawnBonus = getTalentFlatBonus(skillId, 'contractSpawnBonus');
    if (contractSpawnBonus > 0) {
        bonuses.push({ label: 'Contract Spawn Chance', value: `+${(contractSpawnBonus * 100).toFixed(0)}%` });
    }

    const autoQueueBlueprint = getTalentFlatBonus(skillId, 'autoQueueBlueprint');
    if (autoQueueBlueprint > 0) {
        bonuses.push({ label: 'Auto-Queue Blueprint', value: 'Enabled' });
    }

    const dualBlueprint = getTalentFlatBonus(skillId, 'dualBlueprint');
    if (dualBlueprint > 0) {
        bonuses.push({ label: 'Dual Blueprint', value: 'Active (75% speed each)' });
    }

    const contractRarityBoost = getTalentFlatBonus(skillId, 'contractRarityBoost');
    if (contractRarityBoost > 0) {
        bonuses.push({ label: 'Grand Architect', value: 'Contracts always Rare+ (40% Legendary)' });
    }

    const passiveXPBonus = getTalentFlatBonus(skillId, 'passiveXPBonus');
    if (passiveXPBonus > 0) {
        bonuses.push({ label: 'Passive XP Multiplier', value: `+${(passiveXPBonus * 100).toFixed(0)}%` });
    }

    const bloomholdXP = getTalentFlatBonus(skillId, 'bloomholdXP');
    if (bloomholdXP > 0) {
        bonuses.push({ label: 'BloomHold XP', value: `+${bloomholdXP} per action` });
    }

    // Husbandry flat bonuses
    const contractSpeed = getTalentFlatBonus(skillId, 'contractSpeed');
    if (contractSpeed > 0) {
        bonuses.push({ label: 'Contract Speed', value: `+${(contractSpeed * 100).toFixed(0)}%` });
    }

    const feedingEfficiency = getTalentFlatBonus(skillId, 'feedingEfficiency');
    if (feedingEfficiency > 0) {
        bonuses.push({ label: 'Feeding Efficiency', value: `+${(feedingEfficiency * 100).toFixed(0)}%` });
    }

    const chestLootBonus = getTalentFlatBonus(skillId, 'chestLootBonus');
    if (chestLootBonus > 0) {
        bonuses.push({ label: 'Chest Loot Bonus', value: `+${(chestLootBonus * 100).toFixed(0)}%` });
    }

    const packBondSpeed = getTalentFlatBonus(skillId, 'packBondSpeed');
    if (packBondSpeed > 0) {
        bonuses.push({ label: 'Pack Bond Speed', value: `+${(packBondSpeed * 100).toFixed(0)}% per active pet` });
    }

    const feedingOverflow = getTalentFlatBonus(skillId, 'feedingOverflow');
    if (feedingOverflow > 0) {
        bonuses.push({ label: 'Feeding Overflow', value: '+1% per 100 overflow (max +10%)' });
    }

    const dualContracts = getTalentFlatBonus(skillId, 'dualContracts');
    if (dualContracts > 0) {
        bonuses.push({ label: 'Eternal Bond', value: 'Tier 3 pets: 2 contracts' });
    }

    const bountyContractXP = getTalentFlatBonus(skillId, 'bountyContractXP');
    if (bountyContractXP > 0) {
        bonuses.push({ label: 'Bounty Contract XP', value: `+${(bountyContractXP * 100).toFixed(0)}%` });
    }

    const gathererBonusMaterial = getTalentFlatBonus(skillId, 'gathererBonusMaterial');
    if (gathererBonusMaterial > 0) {
        bonuses.push({ label: 'Gatherer Bonus Material', value: `+${(gathererBonusMaterial * 100).toFixed(0)}%` });
    }

    const globalRareLoot = getTalentFlatBonus(skillId, 'globalRareLoot');
    if (globalRareLoot > 0) {
        bonuses.push({ label: 'Global Rare Loot', value: `+${(globalRareLoot * 100).toFixed(0)}%` });
    }

    // Smithing flat bonuses
    const arrowheadOutput = getTalentFlatBonus(skillId, 'arrowheadOutput');
    if (arrowheadOutput > 0) {
        bonuses.push({ label: 'Arrowhead Output', value: `+${(arrowheadOutput * 100).toFixed(0)}%` });
    }

    const fletcherSupply = getTalentFlatBonus(skillId, 'fletcherSupply');
    if (fletcherSupply > 0) {
        bonuses.push({ label: "Fletcher's Supply", value: `+${(fletcherSupply * 100).toFixed(0)}%` });
    }

    const equipmentXPBonus = getTalentFlatBonus(skillId, 'equipmentXPBonus');
    if (equipmentXPBonus > 0) {
        bonuses.push({ label: 'Equipment XP Bonus', value: `+${(equipmentXPBonus * 100).toFixed(0)}%` });
    }

    const furnaceMastery = getTalentFlatBonus(skillId, 'furnaceMastery');
    if (furnaceMastery > 0) {
        bonuses.push({ label: 'Furnace Mastery', value: 'Active (1 ash/5 low, 1 ash/3 high)' });
    }

    // Woodcutting flat bonuses
    const stackingCapBonus = getTalentFlatBonus(skillId, 'stackingCapBonus');
    if (stackingCapBonus > 0) {
        bonuses.push({ label: 'Extended Rhythm', value: `Stack cap +${(stackingCapBonus * 100).toFixed(0)}%` });
    }

    const seedsToBloomHold = getTalentFlatBonus(skillId, 'seedsToBloomHold');
    if (seedsToBloomHold > 0) {
        bonuses.push({ label: 'Fertile Grove', value: 'Seeds → BloomHold' });
    }

    const farmingXPBonus = getTalentFlatBonus(skillId, 'farmingXPBonus');
    if (farmingXPBonus > 0 && skillId === 'woodcutting') {
        bonuses.push({ label: 'Tree Seed Farming XP', value: `+${(farmingXPBonus * 100).toFixed(0)}%` });
    }

    const primeTimber = getTalentFlatBonus(skillId, 'primeTimber');
    if (primeTimber > 0) {
        bonuses.push({ label: 'Prime Timber', value: `+${(primeTimber * 100).toFixed(0)}% Woodworking log value` });
    }

    const rhythmicPrecision = getTalentFlatBonus(skillId, 'rhythmicPrecision');
    if (rhythmicPrecision > 0) {
        bonuses.push({ label: 'Rhythmic Precision', value: `+${(rhythmicPrecision * 100).toFixed(0)}% procs at max stack` });
    }

    const kindlingMaster = getTalentFlatBonus(skillId, 'kindlingMaster');
    if (kindlingMaster > 0) {
        bonuses.push({ label: 'Kindling Master', value: 'Active (1 ash/8 low, 1 ash/5 high)' });
    }

    const naturesBlessing = getTalentFlatBonus(skillId, 'naturesBlessing');
    if (naturesBlessing > 0) {
        bonuses.push({ label: "Nature's Blessing", value: 'Timber! + 1-3 random seeds' });
    }

    // Crafting flat bonuses
    const armorCraftSpeed = getTalentFlatBonus(skillId, 'armorCraftSpeed');
    if (armorCraftSpeed > 0) {
        bonuses.push({ label: 'Armor Craft Speed', value: `+${(armorCraftSpeed * 100).toFixed(0)}%` });
    }

    const highTierXPBonus = getTalentFlatBonus(skillId, 'highTierXPBonus');
    if (highTierXPBonus > 0) {
        bonuses.push({ label: 'High-Tier XP Bonus', value: `+${(highTierXPBonus * 100).toFixed(0)}%` });
    }

    // Combat flat bonuses
    const allDamage = getTalentFlatBonus(skillId, 'allDamage');
    if (allDamage > 0) {
        bonuses.push({ label: 'All Damage', value: `+${(allDamage * 100).toFixed(0)}%` });
    }

    const meleeDamage = getTalentFlatBonus(skillId, 'meleeDamage');
    if (meleeDamage > 0) {
        bonuses.push({ label: 'Melee Damage', value: `+${(meleeDamage * 100).toFixed(0)}%` });
    }

    const rangedDamage = getTalentFlatBonus(skillId, 'rangedDamage');
    if (rangedDamage > 0) {
        bonuses.push({ label: 'Ranged Damage', value: `+${(rangedDamage * 100).toFixed(0)}%` });
    }

    const spellDamage = getTalentFlatBonus(skillId, 'spellDamage');
    if (spellDamage > 0) {
        bonuses.push({ label: 'Spell Damage', value: `+${(spellDamage * 100).toFixed(0)}%` });
    }

    const defense = getTalentFlatBonus(skillId, 'defense');
    if (defense > 0) {
        bonuses.push({ label: 'Defense', value: `+${(defense * 100).toFixed(0)}%` });
    }

    const maxHp = getTalentFlatBonus(skillId, 'maxHp');
    if (maxHp > 0) {
        bonuses.push({ label: 'Max HP', value: `+${Math.floor(maxHp)}` });
    }

    const foodHealing = getTalentFlatBonus(skillId, 'foodHealing');
    if (foodHealing > 0) {
        bonuses.push({ label: 'Food Healing', value: `+${(foodHealing * 100).toFixed(0)}%` });
    }

    const accuracy = getTalentFlatBonus(skillId, 'accuracy');
    if (accuracy > 0) {
        bonuses.push({ label: 'Accuracy', value: `+${(accuracy * 100).toFixed(0)}%` });
    }

    const attackSpeed = getTalentFlatBonus(skillId, 'attackSpeed');
    if (attackSpeed > 0) {
        bonuses.push({ label: 'Attack Speed', value: `+${(attackSpeed * 100).toFixed(0)}%` });
    }

    const glyphCostReduction = getTalentFlatBonus(skillId, 'glyphCostReduction');
    if (glyphCostReduction > 0) {
        bonuses.push({ label: 'Glyph Cost Reduction', value: `-${(glyphCostReduction * 100).toFixed(0)}%` });
    }

    const weaknessBonus = getTalentFlatBonus(skillId, 'weaknessBonus');
    if (weaknessBonus > 0) {
        bonuses.push({ label: 'Weakness Bonus', value: `+${(weaknessBonus * 100).toFixed(0)}%` });
    }

    const spellDurationBonus = getTalentFlatBonus(skillId, 'spellDuration');
    if (spellDurationBonus > 0) {
        bonuses.push({ label: 'Spell Duration', value: `+${(spellDurationBonus * 100).toFixed(0)}%` });
    }

    const bleedDamage = getTalentFlatBonus(skillId, 'bleedDamage');
    if (bleedDamage > 0) {
        bonuses.push({ label: 'Bleed Damage', value: `${bleedDamage} per tick (5 ticks)` });
    }

    const thornDamage = getTalentFlatBonus(skillId, 'thornDamage');
    if (thornDamage > 0) {
        bonuses.push({ label: 'Thorn Armor', value: `${(thornDamage * 100).toFixed(0)}% damage reflected` });
    }

    const combatRegen = getTalentFlatBonus(skillId, 'combatRegen');
    if (combatRegen > 0) {
        bonuses.push({ label: 'Combat Regen', value: `${combatRegen} HP/5s` });
    }

    const berserkerRampage = getTalentFlatBonus(skillId, 'berserkerRampage');
    if (berserkerRampage > 0) {
        bonuses.push({ label: 'Berserker Rampage', value: `+${(berserkerRampage * 100).toFixed(0)}% per kill (max 5)` });
    }

    const lastStand = getTalentFlatBonus(skillId, 'lastStand');
    if (lastStand > 0) {
        bonuses.push({ label: 'Last Stand', value: 'Survive fatal hit at 1 HP (once/fight)' });
    }

    const spellEcho = getTalentFlatBonus(skillId, 'spellEcho');
    if (spellEcho > 0) {
        bonuses.push({ label: 'Spell Echo', value: 'Every 3rd spell casts twice' });
    }

    // Stacking speed bonus (if active)
    const stackingBonus = getStackingSpeedBonus(skillId);
    if (stackingBonus > 0) {
        const config = getStackingSpeedConfig(skillId);
        const currentStacks = getStackCount(skillId);
        // Include Extended Rhythm cap bonus
        const stackingCapBonus = getTalentFlatBonus(skillId, 'stackingCapBonus');
        const effectiveMaxStack = config ? config.maxStack + stackingCapBonus : 0;
        const maxStacks = config ? Math.ceil(effectiveMaxStack / config.value) : 0;
        bonuses.push({
            label: 'Stacking Speed',
            value: `+${(stackingBonus * 100).toFixed(0)}% (${currentStacks}/${maxStacks})`
        });
    }

    return bonuses;
}

// ========================================
// STACKING SPEED SYSTEM
// ========================================

/**
 * Get stackingSpeed effect config for a skill
 * Returns { value, maxStack, proc } or null if no stacking speed talent
 */
export function getStackingSpeedConfig(skillId) {
    const tree = getTalentTreeData(skillId);
    if (!tree) return null;

    const treeData = gameState.getTalentTree(skillId);
    if (!treeData || !treeData.nodes) return null;

    for (const [nodeId, rank] of Object.entries(treeData.nodes)) {
        if (rank <= 0) continue;

        const node = tree.nodes[nodeId];
        if (!node) continue;

        for (const effect of node.effects) {
            if (effect.type === 'stackingSpeed') {
                const value = effect.perRank ? effect.value * rank : effect.value;
                const maxStack = effect.maxStacks || 0.50; // Default 50% max
                return { value, maxStack, proc: effect.proc };
            }
        }
    }

    return null;
}

/**
 * Get current stacking speed bonus for a skill
 * Returns 0 if no stacking speed talent or no stacks
 */
export function getStackingSpeedBonus(skillId) {
    const config = getStackingSpeedConfig(skillId);
    if (!config) return 0;

    const state = stackingSpeedState.get(skillId);
    if (!state || state.count <= 0) return 0;

    // Get Extended Rhythm cap bonus if any
    const stackingCapBonus = getTalentFlatBonus(skillId, 'stackingCapBonus');
    const effectiveMaxStack = config.maxStack + stackingCapBonus;

    // Calculate bonus: value per stack, capped at effective maxStack
    const rawBonus = config.value * state.count;
    const cappedBonus = Math.min(rawBonus, effectiveMaxStack);

    // Apply mastery multiplier
    const masteryMult = getMasteryMultiplier(skillId);
    return cappedBonus * masteryMult;
}

/**
 * Get current stack count for a skill
 */
export function getStackCount(skillId) {
    const state = stackingSpeedState.get(skillId);
    return state ? state.count : 0;
}

/**
 * Increment stacking speed count when an action completes
 * @param skillId - The skill ID
 * @param actionId - Unique identifier for the action (e.g., 'pyrestone_bar', 'common_tree')
 */
export function incrementStackCount(skillId, actionId) {
    const config = getStackingSpeedConfig(skillId);
    if (!config) return;

    let state = stackingSpeedState.get(skillId);

    // If action changed, reset the stack
    if (state && state.lastActionId !== actionId) {
        state = { count: 0, lastActionId: actionId };
    }

    if (!state) {
        state = { count: 0, lastActionId: actionId };
    }

    // Get Extended Rhythm cap bonus if any
    const stackingCapBonus = getTalentFlatBonus(skillId, 'stackingCapBonus');
    const effectiveMaxStack = config.maxStack + stackingCapBonus;

    // Calculate max stacks based on effective config
    const maxStacks = Math.ceil(effectiveMaxStack / config.value);

    // Increment up to max
    if (state.count < maxStacks) {
        state.count++;
    }

    state.lastActionId = actionId;
    stackingSpeedState.set(skillId, state);
}

/**
 * Reset stacking speed count for a skill
 * Called when action stops or changes
 */
export function resetStackCount(skillId) {
    stackingSpeedState.delete(skillId);
}
